import { AsyncPipe, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  DestroyRef,
  EventEmitter,
  Inject,
  OnInit,
  Output,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { createSelector, select, Store } from '@ngrx/store';
import { BehaviorSubject, Observable, pipe, ReplaySubject } from 'rxjs';
import { first, map, switchMap, take } from 'rxjs/operators';

import { HealedVehicle } from 'src/app/api/owcc/models';
import { VehiclesService } from 'src/app/api/owcc/services';
import { appInject } from 'src/app/app-injector';
import { LazyComponentId } from 'src/app/core/services/lazy-components/lazy-components-registry';
import { LinkService } from 'src/app/core/services/link/link.service';
import { TrackClickOnceDirective } from 'src/app/core/services/tracking/directive/track-click-once.directive';
import { TrackingService } from 'src/app/core/services/tracking/tracking.service';
import { selectApiContext } from 'src/app/select-api-context';
import {
  AdditionalChangesInitiator,
  FeasibilityResult
} from 'src/app/shared/feasibility-conflict-modal/feasibility-result.interface';
import { ModalService } from 'src/app/shared/modal/modal-service/modal.service';
import { Notification } from 'src/app/shared/notification/notification';
import { NotificationService } from 'src/app/shared/notification/notification.service';
import { BypassHtmlSecurityPipe } from 'src/app/shared/pipes/bypass-html-security/bypass-html-security.pipe';
import { Nl2brPipe } from 'src/app/shared/pipes/nl2br/nl2br.pipe';
import { StripHtmlPipe } from 'src/app/shared/pipes/strip-html/strip-html.pipe';
import { TranslatePipe } from 'src/app/shared/pipes/translate/translate.pipe';
import { selectVehicleId } from 'src/app/store/runtime-variables/runtime-variables.selectors';
import { goToSummary } from 'src/app/utils/functions/navigate-to-summary';
import { noop } from 'src/app/utils/functions/noop';
import { filterTruthy } from 'src/app/utils/functions/rx-filters';
import { ERROR_MISSING_VEHICLE_CLASS_REDIRECT_PATH } from 'src/app/utils/global-constants';
import { HealingResponse } from 'src/app/utils/models/healing-response.interface';
import { WINDOW } from 'src/app/utils/window.token';

import { RESOLVE_VEHICLE_HEALING_ERROR_TRACKING_DATA } from '../../../notification-tracking';

import { NotificationKey } from './notification-key.enum';

const selectVehicleHealingParams = pipe(
  select(
    createSelector(selectApiContext, selectVehicleId, (apiContext, vehicleId) => ({
      apiContext,
      vehicleId
    }))
  ),
  filterTruthy(({ apiContext, vehicleId }) => !!apiContext && !!vehicleId),
  first(),
  map(({ apiContext, vehicleId }) => ({
    market: apiContext.market,
    subProcess: apiContext.subProcess,
    dataVersion: apiContext.dataVersion,
    vehicleId
  }))
);

@Component({
  selector: 'cc-error-type-resolve-baumuster',
  templateUrl: './resolve-baumuster.component.html',
  styleUrl: './resolve-baumuster.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    NgIf,
    NgTemplateOutlet,
    TrackClickOnceDirective,
    AsyncPipe,
    Nl2brPipe,
    BypassHtmlSecurityPipe,
    StripHtmlPipe,
    TranslatePipe
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ResolveBaumusterComponent implements OnInit {
  @Output() public readonly showLoadingSpinner: EventEmitter<boolean> = new EventEmitter<boolean>();
  @ViewChild('notificationContentFailure', { static: false })
  public readonly notificationContentFailure?: TemplateRef<Element>;
  @ViewChild('notificationContentSuccess', { static: false })
  public readonly notificationContentSuccess?: TemplateRef<Element>;
  @ViewChild('notificationContentDifferences', { static: false })
  public readonly notificationContentDifferences?: TemplateRef<Element>;
  @ViewChild('notificationFooterDifferences', { static: false })
  public readonly notificationFooterDifferences?: TemplateRef<Element>;
  @ViewChild('notificationContentInitial', { static: false })
  public readonly notificationContentInitial?: TemplateRef<Element>;
  @ViewChild('notificationFooterInitial', { static: false })
  public readonly notificationFooterInitial?: TemplateRef<Element>;
  public readonly healingFailed$: Observable<boolean>;

  public readonly trackingData = RESOLVE_VEHICLE_HEALING_ERROR_TRACKING_DATA;

  private readonly healingResponse$: ReplaySubject<HealingResponse> = new ReplaySubject<HealingResponse>(1);
  private readonly healingFailedSubject: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  private healingNotification: Notification | undefined = undefined;

  public constructor(
    private readonly store: Store,
    private readonly linkService: LinkService,
    private readonly router: Router,
    private readonly notificationService: NotificationService,
    private readonly vehiclesService: VehiclesService,
    private readonly trackingService: TrackingService,
    private readonly modalService: ModalService,
    @Inject(WINDOW) public readonly window: Window
  ) {
    this.healingFailed$ = this.healingFailedSubject.asObservable();

    this.healingResponse$.pipe(takeUntilDestroyed()).subscribe({
      error: (): void => {
        this.showHealingError();
      }
    });
  }

  public ngOnInit(): void {
    this.startHealing();
  }

  public restoreConfiguration(): void {
    this.showLoadingSpinner.emit(true);

    this.healingResponse$.pipe(take(1), takeUntilDestroyed(appInject(DestroyRef))).subscribe({
      error: (): void => {
        this.showLoadingSpinner.emit(false);
      },
      next: (response: HealingResponse): void => {
        this.showLoadingSpinner.emit(false);

        if (response.vehicle.vehicleId != null) {
          goToSummary(response.vehicle.vehicleId);
        }

        if (
          response.healingInformation?.healed &&
          (response.healingInformation?.added || response.healingInformation?.removed)
        ) {
          // Same baumuster, slightly different configuration => go to summary, show modal if packages were changed during healing
          const modalData: FeasibilityResult = {
            alternatives: [response.healingInformation],
            changeInitiator: AdditionalChangesInitiator.Healing,
            vehicle: response.vehicle
          };
          this.showHealingChangesModal(modalData);
        } else {
          // Same baumuster, same configuration => go to summary

          this.showHealingNotification();
        }
      }
    });
  }

  public startNewConfiguration(): void {
    this.showLoadingSpinner.emit(true);
    this.hideHealingNotification();

    this.healingResponse$
      .pipe(
        take(1),
        switchMap(
          (response: HealingResponse): Observable<string> =>
            this.linkService.getMotorizationLink(response.vehicle.vehicleClassId, response.vehicle.vehicleBodyId)
        ),
        takeUntilDestroyed(appInject(DestroyRef))
      )
      .subscribe({
        next: (link: string): void => {
          this.showLoadingSpinner.emit(false);
          this.router.navigate([link]).catch(noop);
        },
        error: (): void => {
          // If healing failed we have no details about the previous vehicle so we have to go to the main configurator page

          // The protocol is intentionally missing to support both http and https, the browser automatically uses the currently in-use protocol
          this.window.location.href = `//${
            this.window.location.host
          }/${ERROR_MISSING_VEHICLE_CLASS_REDIRECT_PATH.replace(/^[/\\]+/, '')}`;
        }
      });
  }

  public continueConfiguration(): void {
    this.showLoadingSpinner.emit(true);
    this.hideHealingNotification();

    this.healingResponse$.pipe(take(1), takeUntilDestroyed(appInject(DestroyRef))).subscribe({
      next: (response: HealingResponse): void => {
        this.showLoadingSpinner.emit(false);

        if (response.vehicle.vehicleId != null) {
          goToSummary(response.vehicle.vehicleId);
        }
      },
      error: (): void => {
        this.showLoadingSpinner.emit(false);
      }
    });
  }

  private startHealing(): void {
    // dont do all the stuff in here. needs to be moved to effects, Ticket: https://jira.mercedes-benz.io/browse/MBCC-685

    const result = this.store.pipe(
      selectVehicleHealingParams,
      switchMap(({ market, subProcess, dataVersion, vehicleId }) =>
        this.vehiclesService.healVehicle({
          market,
          subProcess: subProcess || '',
          dataVersion: dataVersion || '',
          vehicleId: vehicleId || ''
        })
      ),
      map(
        (response: HealedVehicle): HealingResponse => ({
          vehicle: response.vehicle || null,
          healingInformation: {
            healed: !!response.healingInformation?.healed,
            initialConfiguration: !!response.healingInformation?.initialConfiguration,
            added: response.healingInformation?.added,
            removed: response.healingInformation?.removed
          }
        })
      )
    );

    result.subscribe({
      next: (response: HealingResponse): void => {
        this.healingResponse$.next(response);
        this.healingResponse$.complete();
      },
      error: (error: unknown): void => {
        this.healingResponse$.error(error);
        this.healingResponse$.complete();
      }
    });
  }

  private showHealingNotification(): void {
    const content = this[`notificationContent${NotificationKey.Initial}`];
    const footer = this[`notificationFooter${NotificationKey.Initial}`];

    if (content && footer) {
      this.healingNotification = {
        icon: '',
        variant: 'warning',
        autoDismiss: false,
        dismissable: false,
        content: content as string | TemplateRef<Element>,
        footer: footer as string | TemplateRef<Element>
      };

      this.notificationService.notify(this.healingNotification);
      this.trackingService.sendTrackingData(this.trackingData.impressionEvent);
    }
  }

  private showHealingChangesModal(healingInformation: FeasibilityResult): void {
    this.modalService.open(LazyComponentId.FeasibilityConflictModal, healingInformation);

    this.trackingService.sendTrackingData(this.trackingData.impressionEvent);
  }

  private showHealingError(): void {
    this.healingFailedSubject.next(true);
  }

  private hideHealingNotification(): void {
    if (this.healingNotification) {
      this.notificationService.clear(this.healingNotification);
      this.healingNotification = undefined;
    }
  }
}
